import { FormatToShortPriceTemplate } from '../../../../utils/FormatValue';
import { MonthYearValue } from '../../../../utils/MonthYear/MonthYearValue';
import { MonthYear, months } from '../../../../utils/MonthYear/monthYear';
import { IChartData, DEFAULT_COLOR } from './DateValueInputMultiChart';

export const filterData = (
  data: MonthYearValue[],
  numberOfMonthsToShow: number,
  startingDate: MonthYear,
): MonthYearValue[] => {
  if (!data) return [];
  const sortedData = data.sort((a, b) =>
    a.date.year !== b.date.year ? a.date.year - b.date.year : a.date.month - b.date.month,
  );

  const result: MonthYearValue[] = [];
  let currentDate = startingDate;
  let dataIndex = sortedData.findIndex(
    (item) =>
      item.date.year > startingDate.year ||
      (item.date.year === startingDate.year && item.date.month >= startingDate.month),
  );
  if (dataIndex === -1) {
    dataIndex = sortedData.length;
  }

  for (let i = 0; i < numberOfMonthsToShow; i++) {
    if (
      dataIndex < sortedData.length &&
      sortedData[dataIndex].date.year === currentDate.year &&
      sortedData[dataIndex].date.month === currentDate.month
    ) {
      result.push(sortedData[dataIndex]);
      dataIndex++;
    } else {
      result.push({ value: 0, date: currentDate });
    }
    currentDate = addMonths(currentDate, 1);
  }

  return result;
};

export const addMonths = (date: MonthYear, monthsToAdd: number): MonthYear => {
  const newMonth = date.month + monthsToAdd;
  const newYear = date.year + Math.floor((newMonth - 1) / 12);
  return {
    month: ((newMonth - 1) % 12) + 1,
    year: newYear,
  };
};

export const getMultiChartOptions = (
  mainChart: IChartData,
  additionalCharts?: IChartData[],
  suggestedMaxValue?: number,
  suggestedMinValue?: number,
) => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const,
      },
      title: {
        display: false,
        text: ``,
      },
    },
    scales: {
      x: {
        type: 'category' as const,
        grid: {
          color: ({ tick }: any) => {
            const { month, year } = mainChart.data.length > 0 ? mainChart.data[0].date : { month: 1, year: 2000 };
            return tick?.label === `${months[month - 1]} ${year.toString().slice(2, 4)}` ? '#afb0b0' : '#E0E0E0';
          },
          borderColor: (context: any) => {
            const firstDate = mainChart.data.length > 0 ? mainChart.data[0].date : { month: 1, year: 2000 };
            if (context.tick && context.tick.label) {
              const monthYearLabel = `${months[firstDate.month - 1]} ${firstDate.year.toString().slice(2, 4)}`;
              if (context.tick.label === monthYearLabel) {
                return '#afb0b0';
              }
            }
            return '#E0E0E0';
          },
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        beginAtZero: true,
        title: {
          display: true,
          text: mainChart.title,
        },
        ticks: {
          beginAtZero: true,
          callback: function (value: any) {
            return FormatToShortPriceTemplate(value, '');
          },
        },
        suggestedMin: suggestedMinValue,
        suggestedMax: suggestedMaxValue,
        grid: {
          color: (context: any) => {
            return context.tick?.value === 0 ? '#afb0b0' : '#E0E0E0';
          },
          borderColor: (context: any) => {
            return context.tick?.value === 0 ? '#afb0b0' : '#E0E0E0';
          },
        },
      },
      ...(additionalCharts?.reduce((acc: any, chart, index) => {
        acc[`y${index + 2}`] = {
          type: 'linear' as const,
          display: false,
          suggestedMin: suggestedMinValue,
          suggestedMax: suggestedMaxValue,
        };
        return acc;
      }, {}) || {}),
    },
  };
};
export const getMuiltiChartData = (
  mainChart: IChartData,
  numberOfMonths: number,
  startingDate: MonthYear,
  additionalChartsData?: IChartData[],
) => {
  const filteredChartData = filterData(mainChart.data, numberOfMonths, startingDate);
  return {
    labels: filteredChartData.map((d) => `${months[d.date.month - 1]} ${d.date.year.toString().slice(2, 4)}`),
    datasets: [
      {
        type: mainChart.type,
        label: mainChart.title,
        data: filteredChartData.map((item) => (mainChart.isFlipped ? -item.value : item.value)),
        borderWidth: 1,
        borderColor: mainChart.color || DEFAULT_COLOR,
        backgroundColor: mainChart.transparent ? 'transparent' : mainChart.color || DEFAULT_COLOR,
        yAxisID: 'y1',
        order: 1,
        barThickness: mainChart.barThickness ? mainChart.barThickness : ('flex' as const),
        categoryPercentage: 0.6,
        barPercentage: 0.7,
      },
      ...(additionalChartsData?.map((chart) => ({
        type: chart.type,
        label: chart.title,
        data: filterData(chart.data, numberOfMonths, startingDate).map((item) =>
          chart.isFlipped ? -item.value : item.value,
        ),
        borderWidth: 1,
        borderColor: chart.color || DEFAULT_COLOR,
        backgroundColor: chart.transparent ? 'transparent' : chart.color || DEFAULT_COLOR,
        yAxisID: 'y1',
        barThickness: 'flex' as const,
        categoryPercentage: 0.6,
        barPercentage: 0.7,
      })) || []),
    ],
  };
};
